import { useEffect, useState } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/carousel";
import { cn } from "~/lib/utils";
import OutfitButtons from "./outfit-buttons";
import { useRouter } from "next/router";
import { REFS } from "~/lib/constants/refs";
import { useFaceSwap } from "../providers/faceswap-provider";
import { useSession } from "next-auth/react";
import {
  type OutfitProductBaseProps,
  OutfitProductProvider,
} from "~/components/outfit-product/outfit-product-provider";
import {
  OutfitProductActions,
  OutfitProductCard,
  OutfitProductImage,
  OutfitProductTags,
} from "~/components/outfit-product/outfit-product";

export interface Outfit {
  id: string;
  image_url: string;
  faceswap_image_url?: string | null;
  created_for_user_id?: string | null;
  products: OutfitProductBaseProps[];
}

type Props = {
  outfits: Outfit[];
  withButtons?: boolean;
};

const OutfitWithItemsCard = ({ outfits, withButtons }: Props) => {
  const [activeId, setActiveId] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const selectedOutfit = outfits[activeId];
  const isMobile = getIsMobile();

  useEffect(() => {
    if (!api) return;
    api.on("select", () => {
      setActiveId(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className="w-full">
      <Carousel
        className="w-full"
        setApi={setApi}
        opts={{
          // positions the carousel items in the center
          containScroll: false,
          loop: true,
        }}
      >
        <CarouselContent className="gap-3">
          {outfits.map((o, i) => (
            <OutfitCarouselItem
              key={o.id}
              outfit={o}
              withButtons={withButtons}
              selected={i === activeId}
              onClick={() => api?.scrollTo(i)}
            />
          ))}
        </CarouselContent>

        <div className="absolute top-1/2 w-full px-8 sm:px-4 md:px-0">
          <div className="relative mx-auto h-0 w-full md:max-w-3xl">
            <CarouselPrevious
              variant="custom"
              className={cn(
                "absolute left-0 top-0 z-10",
                "bg-background text-foreground",
                "border-2 border-slate-200",
              )}
            />
            <CarouselNext
              variant="custom"
              className={cn(
                "absolute right-0 top-0 z-10",
                "bg-background text-foreground",
                "border-2 border-slate-200",
              )}
            />
          </div>
        </div>
      </Carousel>

      {/* Pagination dots (desktop only) */}
      <div className="mt-5 hidden justify-center gap-2 sm:flex">
        {outfits.map((_, i) => (
          <button
            key={i}
            className={cn(
              "h-5 w-5 rounded-full border-[2.5px] border-slate-200 bg-background shadow-sm",
              i === activeId && "border-slate-600",
            )}
            onClick={() => api?.scrollTo(i)}
          />
        ))}
      </div>

      {/* Mobile outfit breakdown */}
      {selectedOutfit && (
        <OutfitProducts
          id={isMobile ? REFS.OUTFIT_ITEM : ""}
          className="no-scrollbar mt-4 flex w-full flex-row gap-2 overflow-x-auto px-4 py-3 sm:hidden"
          outfit={selectedOutfit}
          withButtons={withButtons}
        />
      )}
    </div>
  );
};

interface OutfitCarouselItemProps {
  outfit: Outfit;
  withButtons?: boolean;
  selected: boolean;
  onClick: () => void;
}

const OutfitCarouselItem = ({
  outfit,
  withButtons,
  selected,
  onClick,
}: OutfitCarouselItemProps) => {
  const router = useRouter();
  const session = useSession();
  const { faceSwapEnabled } = useFaceSwap();
  const isMobile = getIsMobile();

  const search = () => {
    void router.push({
      pathname: "/search",
      query: {
        search_type: "OUTFIT_PRODUCT",
        outfit_id: outfit.id,
      },
    });
  };

  function getImgSource() {
    if (!faceSwapEnabled) return outfit.image_url;
    if (!outfit.faceswap_image_url) return outfit.image_url;
    if (!outfit.created_for_user_id) return outfit.image_url;
    const userId = session?.data?.user?.id;
    if (userId !== outfit.created_for_user_id) return outfit.image_url;
    return outfit.faceswap_image_url;
  }

  return (
    <CarouselItem
      key={outfit.id}
      className={cn(
        // Mobile styles (outfit image only in carousel)
        "w-full max-w-[70%]",
        // Desktop styles (always square)
        // Max width to always fit another item on the right
        "sm:aspect-square sm:max-w-[75%] md:max-w-2xl",
        // No padding on mobile
        // Desktop padding of other edges done by children to allow nicer scrolling
        "pl-0 first:ml-3 sm:pl-3",
        // All devices (though gap is only relevant on desktop)
        "group flex flex-row items-start gap-3 rounded-lg",
        "border bg-background transition-opacity duration-200",
        !selected && "opacity-80 sm:opacity-50 sm:blur-sm",
      )}
      onClick={selected ? undefined : () => onClick()}
    >
      <div
        className={cn(
          "relative h-full w-full sm:w-1/2 sm:py-3",
          !selected && "pointer-events-none",
        )}
      >
        <img
          src={getImgSource()}
          alt="Outfit"
          className="h-full w-full cursor-pointer rounded-lg object-cover"
          onClick={search}
        />
        {withButtons && (
          <div className="absolute bottom-3 left-0 flex w-full justify-center md:bottom-5">
            <OutfitButtons id={outfit.id} onSearch={search} />
          </div>
        )}
      </div>
      <OutfitProducts
        id={isMobile ? "" : REFS.OUTFIT_ITEM}
        className={cn(
          "hidden flex-col gap-2 sm:flex",
          "max-h-full w-1/2 overflow-y-auto py-3 pr-3",
          !selected && "pointer-events-none",
        )}
        outfit={outfit}
        withButtons={withButtons}
      />
    </CarouselItem>
  );
};

interface OutfitProductsProps {
  id?: string;
  className?: string;
  outfit: Outfit;
  withButtons?: boolean;
}

const OutfitProducts = ({
  id,
  className,
  outfit,
  withButtons,
}: OutfitProductsProps) => {
  return (
    <div id={id} className={className}>
      {outfit.products.map((p) => (
        <OutfitProductProvider key={p.id} outfit={outfit} product={p}>
          <OutfitProductCard>
            <OutfitProductImage />
            <OutfitProductTags />
            {withButtons && <OutfitProductActions />}
          </OutfitProductCard>
        </OutfitProductProvider>
      ))}
    </div>
  );
};

export const OutfitWithItemsCardLoading = () => {
  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center sm:flex-row",
        "animate-pulse duration-1000",
      )}
    >
      <div
        className={cn(
          "w-full max-w-[50%]",
          "sm:aspect-square sm:max-w-[75%] md:max-w-2xl",
          "pl-0 sm:pl-3",
          "group flex flex-row items-start gap-3 rounded-lg",
          "bg-background duration-200",
          "opacity-80 blur-sm sm:opacity-50",
        )}
      >
        <div className="aspect-[3/5] h-full w-full sm:w-1/2 sm:py-3">
          <div className="h-full w-full bg-muted" />
        </div>
        <div className="hidden max-h-full w-1/2 flex-col gap-2 overflow-y-auto py-3 pr-3 sm:flex">
          <div className="aspect-square w-full bg-muted" />
          <div className="aspect-square w-full bg-muted" />
        </div>
      </div>
      <div className="flex w-full flex-row gap-2 overflow-x-auto px-4 py-3 sm:hidden">
        <div className="aspect-square w-full rounded-lg bg-muted" />
        <div className="aspect-square w-full rounded-lg bg-muted" />
      </div>
    </div>
  );
};

function getIsMobile() {
  const vw = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
  );
  return vw < 640;
}

export default OutfitWithItemsCard;
